import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService, User } from '../services/auth.service';
import { MessagesService } from '../services/messages.service';
import { ReverseImageSearchService } from '../services/reverse-image-search.service';
import { ReverseImageSearchModalComponent } from '../reverse-image-search-modal/reverse-image-search-modal.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReverseImageSearchModalComponent,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];
  private messageSubscription?: Subscription;
  private tooltipTimeout?: number;
  private scrollPosition = 0;
  private readonly MOBILE_BREAKPOINT = 768;

  isMenuOpen = false;
  isProfileMenuOpen = false;
  isSearchVisible = false;
  isCategoryDropdownOpen = false;
  isReverseImageSearchModalOpen = false;
  isServiceUnavailableTooltipVisible = false;
  reverseImageSearchAvailable = false;
  user: User | null = null;
  isLoggedIn = false;
  unreadMessageCount = 0;

  constructor(
    private authService: AuthService,
    private router: Router,
    private messagesService: MessagesService,
    private reverseImageSearchService: ReverseImageSearchService
  ) {}

  ngOnInit(): void {
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.user = user;
        this.isLoggedIn = !!user;
      });

    this.authService.token$
      .pipe(takeUntil(this.destroy$))
      .subscribe((token) => {
        this.isLoggedIn = !!token;
      });

    this.authService.getCurrentUser().subscribe();

    const authSub = this.authService.isLoggedIn$.subscribe((isLoggedIn) => {
      if (isLoggedIn) {
        if (this.messageSubscription) {
          this.messageSubscription.unsubscribe();
        }
        this.messageSubscription = this.messagesService.unreadCount$.subscribe(
          (count) => {
            this.unreadMessageCount = count;
          }
        );
        this.messagesService.refreshUnreadCount();
      } else {
        this.unreadMessageCount = 0;
        if (this.messageSubscription) {
          this.messageSubscription.unsubscribe();
          this.messageSubscription = undefined;
        }
      }
    });
    this.subscriptions.push(authSub);

    this.checkReverseImageSearchAvailability();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.isMenuOpen) {
          this.closeMenuAndRestoreScroll();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
    }
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }
    this.restoreBodyScroll();
    this.unlockBodyScroll();
  }

  private checkReverseImageSearchAvailability(): void {
    this.reverseImageSearchService
      .isEnabled()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (available) => {
          this.reverseImageSearchAvailable = available;
        },
        error: () => {
          this.reverseImageSearchAvailable = false;
        },
      });
  }

  private restoreBodyScroll(): void {
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';

    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  }

  private closeMenuAndRestoreScroll(): void {
    this.isMenuOpen = false;
    this.isCategoryDropdownOpen = false;
    this.restoreBodyScroll();
  }

  getUnreadBadge(): string {
    if (this.unreadMessageCount === 0) return '';
    if (this.unreadMessageCount > 99) return '99+';
    return this.unreadMessageCount.toString();
  }

  hasUnreadMessages(): boolean {
    return this.unreadMessageCount > 0;
  }

  onSearchKeydown(event: KeyboardEvent, searchTerm: string): void {
    if (
      event.key === 'Enter' ||
      event.code === 'Enter' ||
      event.keyCode === 13
    ) {
      event.preventDefault();
      this.performSearch(searchTerm);
    }
  }

  performSearch(searchTerm: string): void {
    const searchKeyword = searchTerm.trim();
    if (!searchKeyword) {
      alert('Please enter what you are looking for');
      return;
    }
    this.router.navigate(['/search', 'all', encodeURIComponent(searchKeyword)]);
  }

  getImageSearchTooltip(): string {
    if (!this.reverseImageSearchAvailable) {
      return 'AI image search is currently unavailable';
    }
    return 'Search by image using AI';
  }

  getServiceUnavailableMessage(): string {
    return 'AI Image Search is currently unavailable';
  }

  getMobileSearchText(): string {
    if (!this.reverseImageSearchAvailable) {
      return 'AI Search (Unavailable)';
    }
    return 'AI Image Search';
  }

  openReverseImageSearchModal(): void {
    if (!this.reverseImageSearchAvailable) {
      this.showServiceUnavailableTooltip();
      return;
    }

    this.isReverseImageSearchModalOpen = true;
    this.lockBodyScroll();
  }

  closeReverseImageSearchModal(): void {
    this.isReverseImageSearchModalOpen = false;
    this.unlockBodyScroll();
  }

  showServiceUnavailableTooltip(): void {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }

    this.isServiceUnavailableTooltipVisible = true;

    this.tooltipTimeout = window.setTimeout(() => {
      this.isServiceUnavailableTooltipVisible = false;
    }, 3000);
  }

  hideServiceUnavailableTooltip(): void {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }
    this.isServiceUnavailableTooltipVisible = false;
  }

  navigateToWishlist(): void {
    this.router.navigate(['/wishlist']);
    if (this.isMenuOpen) {
      this.closeMenuAndRestoreScroll();
    }
    this.isProfileMenuOpen = false;
    this.isCategoryDropdownOpen = false;
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;

    if (this.isMenuOpen) {
      this.isProfileMenuOpen = false;

      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      this.restoreBodyScroll();
      this.isCategoryDropdownOpen = false;
    }
  }

  toggleProfileMenu(): void {
    this.isProfileMenuOpen = !this.isProfileMenuOpen;
    if (this.isProfileMenuOpen) {
      this.isMenuOpen = false;
    }
  }

  toggleCategoryDropdown(): void {
    if (window.innerWidth <= 1024) {
      this.isCategoryDropdownOpen = !this.isCategoryDropdownOpen;
    }
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
    if (this.isSearchVisible) {
      setTimeout(() => {
        const searchInput = document.querySelector(
          '.search-input'
        ) as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    }
  }

  logout(): void {
    this.authService.logout().subscribe({
      next: (response) => {
        this.router.navigate(['/login']);
      },
      error: (error) => {
        console.error('⏱️ Header: Logout error', error);
        this.router.navigate(['/login']);
      },
      complete: () => {
        this.closeAllMenus();
      },
    });
  }

  private lockBodyScroll(): void {
    if (
      typeof window !== 'undefined' &&
      window.innerWidth <= this.MOBILE_BREAKPOINT
    ) {
      this.scrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }
  }

  private unlockBodyScroll(): void {
    if (
      typeof window !== 'undefined' &&
      window.innerWidth <= this.MOBILE_BREAKPOINT
    ) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, this.scrollPosition);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const profileContainer = document.querySelector('.action-profile');
    const profileBtn = document.querySelector('.btn-profile');

    if (
      this.isProfileMenuOpen &&
      profileContainer &&
      !profileContainer.contains(event.target as Node) &&
      profileBtn &&
      !profileBtn.contains(event.target as Node)
    ) {
      this.isProfileMenuOpen = false;
    }

    const searchContainer = document.querySelector('.search-wrapper');
    if (
      this.isServiceUnavailableTooltipVisible &&
      searchContainer &&
      !searchContainer.contains(event.target as Node)
    ) {
      this.hideServiceUnavailableTooltip();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (window.innerWidth > 1024) {
      if (this.isMenuOpen) {
        this.closeMenuAndRestoreScroll();
      }
      this.isCategoryDropdownOpen = false;
    }

    if (this.isReverseImageSearchModalOpen) {
      if (window.innerWidth <= this.MOBILE_BREAKPOINT) {
        this.lockBodyScroll();
      } else {
        this.unlockBodyScroll();
      }
    }
  }

  closeAllMenus(): void {
    if (this.isMenuOpen) {
      this.closeMenuAndRestoreScroll();
    }
    this.isProfileMenuOpen = false;
    this.isCategoryDropdownOpen = false;
    this.isSearchVisible = false;
    this.hideServiceUnavailableTooltip();
  }
}
